/**
 * Integration Section Component
 *
 * Reusable component for displaying integration configuration sections
 */

import { Box, Typography, TextField, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

interface IntegrationField {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    helperText?: string;
}

interface IntegrationSectionProps {
    title: string;
    description: string;
    fields: IntegrationField[];
    onReset: () => void;
    onValidate: () => void;
    hasChanges: boolean;
    isValid: boolean;
    isSaving: boolean;
}

export const IntegrationSection = ({
    title,
    description,
    fields,
    onReset,
    onValidate,
    hasChanges,
    isValid,
    isSaving,
}: IntegrationSectionProps) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                paddingBottom: "24px",
                borderBottom: "1px solid #E7E6E4",
                "&:last-child": {
                    borderBottom: "none",
                    paddingBottom: 0,
                },
            }}
        >
            {/* Section Header */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                }}
            >
                <Typography variant="h4" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                    {title}
                </Typography>
                <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#757575" }}>
                    {description}
                </Typography>
            </Box>

            {/* Fields */}
            {fields.map((field, index) => (
                <Box
                    key={index}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: "17px",
                        width: "100%",
                        maxWidth: "700px",
                    }}
                >
                    {/* Field Label and Input */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            flex: 1,
                        }}
                    >
                        {field.label && (
                            <Typography variant="small-medium" sx={{ color: "#393A36", fontWeight: 500 }}>
                                {field.label}
                            </Typography>
                        )}
                        <TextField
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder={field.placeholder || ""}
                            disabled={isSaving}
                            helperText={field.helperText}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                    "&.Mui-disabled fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "0px 12px 0px 20px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                                "& .MuiFormHelperText-root": {
                                    marginLeft: "20px",
                                    fontSize: "0.75rem",
                                    color: "#757575",
                                },
                            }}
                        />
                    </Box>
                </Box>
            ))}

            {/* Buttons */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "8px",
                }}
            >
                {/* Reset Button */}
                <Button
                    onClick={onReset}
                    disabled={!hasChanges || isSaving}
                    sx={{
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        padding: "0px 20px",
                        height: "40px",
                        minWidth: "auto",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                        "&:disabled": {
                            opacity: 0.36,
                            backgroundColor: "transparent",
                            color: "#393A36",
                            border: "1px solid #E7E6E4",
                        },
                    }}
                >
                    {t("integrations.reset", "Reset")}
                </Button>

                {/* Validate Button */}
                <Button
                    onClick={onValidate}
                    disabled={!hasChanges || !isValid || isSaving}
                    sx={(theme) => ({
                        width: "140px",
                        height: "40px",
                        borderRadius: "44px",
                        padding: "0px 20px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        textTransform: "none",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            color: "white !important",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {isSaving ?
                        <CircularProgress size={20} color="inherit" />
                    :   t("integrations.validate", "Validate")}
                </Button>
            </Box>
        </Box>
    );
};
